import { Res, ValueButtonSize, PosType, ScreenScrollMode } from 'core/api';

let deviceType: PosType = PosType.SMARTPHONE;

export type Theme = { name: string, value: 'light' | 'dark' };

export function getAllThemes(): Theme[] {
	return [
		{ name: Res.Web.POS_CONFIGURATION_LIGHT_THEME(), value: 'light' },
		{ name: Res.Web.POS_CONFIGURATION_DARK_THEME(), value: 'dark' }
	];
}

export function activateTheme(themeValue: string): void {

	// Elimino el tema actual. No uso una expresión regular porque hay otras
	// cosas que son theme-XXX que no son temas, como la configuración del tamaño de botones
	getAllThemes().forEach(({ value }) => document.body.classList.remove(`theme-${value}`));

	document.body.classList.add(`theme-${themeValue}`);

	// Color el chrome del navegador en Android
	const themeColor = getComputedStyle(document.body).getPropertyValue('--color-blue');
	document.querySelector('meta[name="theme-color"]')!.setAttribute('content', themeColor);
}

export function getCurrentTheme(): Theme {
	return getAllThemes().find(x => document.body.classList.contains(`theme-${x.value}`))!;
}

export function setButtonSize(size: ValueButtonSize) {
	if (size === ValueButtonSize.Large)
		document.body.classList.add('theme-large-buttons');
	if (size === ValueButtonSize.Small)
		document.body.classList.add('theme-small-buttons');
}

export function setDeviceType(type: PosType) {
	const posTypeToClassName = {
		[PosType.SMARTPHONE]: 'theme-phone',
		[PosType.POS]: 'theme-tablet',
		[PosType.TABLET]: 'theme-tablet',
	};
	document.body.classList.remove(...Object.values(posTypeToClassName));
	document.body.classList.add(posTypeToClassName[type]);

	// Necesito una clase específica para pos porque quiero gestionar de manera
	// diferente las barras de scroll en pos (que asumo que es windows) que en
	// tablet (que asumo que es android/ios). No quiero quitar theme-tablet
	// a los pos porque hay muchas reglas de estilo que se basan en ella, así
	// que añado una clase adicional, manteniendo también la que había.
	if (type === PosType.POS)
		document.body.classList.add('theme-pos');
	else
		document.body.classList.remove('theme-pos');

	deviceType = type;

	// Desactivo la ayuda por si hemos pasado a un tipo de dispositivo que
	// no soporta mostrar ayuda en los botones
	if (helpIsActivated())
		toggleHelp();
}

export function getDeviceType() {
	return deviceType;
}

export function isPhone() {
	return deviceType === PosType.SMARTPHONE;
}

export function isTablet() {
	return deviceType === PosType.TABLET;
}

export function isWebPos() {
	return deviceType === PosType.POS;
}

export function isLargeDevice() {
	return deviceType !== PosType.SMARTPHONE;
}

export function toggleHelp(): void {

	const classes = document.body.classList;

	if (classes.contains('theme-show-help'))
		classes.remove('theme-show-help');
	else
		classes.add('theme-show-help');
}

export function helpIsActivated(): boolean {
	const classes = document.body.classList;
	return classes.contains('theme-show-help');
}

export function setScrollMode(mode: ScreenScrollMode) {

	const classes = document.body.classList;
	classes.remove('hide-scrollbars');
	classes.remove('show-scrollbars');

	if (mode === ScreenScrollMode.Hidden)
		classes.add('hide-scrollbars');
	else if (mode === ScreenScrollMode.Visible)
		classes.add('show-scrollbars');
}


export function selectScreen<P>({ Phone, Tablet, WebPos }: { Phone: React.ComponentClass<P>, Tablet: React.ComponentClass<P>, WebPos?: React.ComponentClass<P> }): React.ComponentClass<P> {

	if (![PosType.SMARTPHONE, PosType.TABLET, PosType.POS].includes(deviceType))
		throw Error('Esta aplicación no soporta puntos de venta de tipo PC');

	// Si no tengo un WebPos específico, hago un fallback a la pantalla de Tablet
	if (deviceType === PosType.POS)
		return WebPos || Tablet;

	return deviceType === PosType.SMARTPHONE ? Phone : Tablet;
}


